// src/shared/LocationProgress.jsx
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Прогресс при смене location для BrowserRouter:
 * - Стартует при изменении pathname.
 * - Растёт до ~90%.
 * - Через короткую задержку добегает до 100% и скрывается.
 * - Имеет анти-мигание для очень быстрых переходов.
 */
export default function LocationProgress({
  height = 3,
  maxWhilePending = 90,
  showDelayMs = 120,
  minVisibleMs = 250,
  finishDelayMs = 220,
}) {
  const loc = useLocation();
  const [visible, setVisible] = useState(false);
  const [width, setWidth] = useState(0);

  const startedAt = useRef(0);
  const growTO = useRef();
  const showTO = useRef();
  const finishTO = useRef();

  // Запуск при смене пути
  useEffect(() => {
    clearInterval(growTO.current);
    clearTimeout(showTO.current);
    clearTimeout(finishTO.current);

    showTO.current = setTimeout(() => {
      startedAt.current = Date.now();
      setVisible(true);
      setWidth(15);

      growTO.current = setInterval(() => {
        setWidth((w) => Math.min(maxWhilePending, w + Math.max(1, (maxWhilePending - w) * 0.08)));
      }, 120);
    }, showDelayMs);

    return () => {
      clearInterval(growTO.current);
      clearTimeout(showTO.current);
      clearTimeout(finishTO.current);
    };
  }, [loc.pathname, showDelayMs, maxWhilePending]);

  // Фаза финиша — автоматически после маунта новой страницы
  useEffect(() => {
    if (!visible) return;

    const elapsed = Date.now() - startedAt.current;
    const wait = Math.max(0, minVisibleMs - elapsed);

    finishTO.current = setTimeout(() => {
      setWidth(100);
      setTimeout(() => {
        setVisible(false);
        setWidth(0);
      }, finishDelayMs);
    }, wait);

    return () => clearTimeout(finishTO.current);
  }, [visible, minVisibleMs, finishDelayMs]);

  if (!visible) return null;

  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(width)}
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        height,
        width: `${width}%`,
        zIndex: 10000,
        transition: "width .2s ease",
        background: "var(--accent, #4f46e5)",
        boxShadow: "0 1px 2px rgba(0,0,0,.12)",
      }}
    />
  );
}
